<template>
  <div class="w-100">
    <HotelTopNavbar />
    <block :height="83" />
    <div :class="$is_mobile_view ? 'mb-4' : 'my-5'">
      <HotelCheckoutStepper :steps="steps" :current-step="currentStep" />
    </div>

    <AdditionalService
      @update-step="updateCurrentStep"
      v-if="sourceModule === 'additional-service'" />
    <template v-else>
      <Mobile v-if="is_mobile" @update-step="updateCurrentStep" @sendTrackEvent="sendTrackingEvent"></Mobile>
      <Desktop v-else @update-step="updateCurrentStep" @sendTrackEvent="sendTrackingEvent"></Desktop>
    </template>

    <Footer />
  </div>
</template>
<script>
import Desktop from '@/pages/hotel/be/BePaymentResultDesktop';
import Mobile from '@/pages/hotel/be/BePaymentResultMobile';
import AdditionalService from './BePaymentResultAdditionalService.vue';
import GeneralTopNavbar from '@/components/general/GeneralTopNavbar.vue';
import HotelTopNavbar from '../../../components/hotel/HotelTopNavbar.vue';
import HotelCheckoutStepper from '../../../components/hotel/be/HotelCheckoutStepper.vue';
import Footer from '@/components/hotel/Footer.vue';
import { mapGetters } from 'vuex';
import { trackEvent } from '../../../utils/analytics';
import { post } from '../../../services';
import moment from 'moment';

export default {
  components: {
    Desktop,
    Mobile,
    AdditionalService,
    GeneralTopNavbar,
    Footer,
    HotelTopNavbar,
    HotelCheckoutStepper,
  },
  data() {
    return {
      is_mobile: true,
      steps: [
        { order: 1, title: 'Your Selection' },
        { order: 2, title: 'Your Details' },
        { order: 3, title: 'Final Step' },
      ],
      viewType: 'FORM', // ["FORM", "PAYMENT"]
      currentStep: 3,
    };
  },
  computed: {
    ...mapGetters(['getMerchant']),
    sourceModule() {
      return this.$isNotEmptyV2(this.$route.query.module)
        ? this.$route.query.module
        : 'room';
    },
    transactionId(){
      return this.$route.params.tx_order_id
    },
  },
  created() {
    if (window.innerWidth > 768) {
      this.is_mobile = false;
    }
  },
  methods: {
    updateCurrentStep(newStep) {
      this.currentStep = newStep;
    },
    async sendTrackingEvent(detailTransaction){
      const TRACKING_PROVIDERS = [
        {
          type: 'google_tag_manager',
          configKeys: {
            use: 'use_google_tag_manager_id',
            id: 'google_tag_manager_id',
          },
          dateKeys: {
            pending: 'google_tag_manager_posting_date',
            complete: 'google_tag_manager_COMPLETE_posting_date',
            cancelled: 'google_tag_manager_CANCELLED_posting_date',
          },
        },
        {
          type: 'google_analytics',
          configKeys: {
            use: 'use_google_analytics_id',
            id: 'google_analytics_id',
          },
          dateKeys: {
            pending: 'google_analytics_posting_date',
            complete: 'google_analytics_COMPLETE_posting_date',
            cancelled: 'google_analytics_CANCELLED_posting_date',
          },
        },
        {
          type: 'hijiffy',
          configKeys: {
            use: 'use_hijiffy',
            id: 'hijiffy_account_id',
          },
          dateKeys: {
            pending: 'hijiffy_posting_date',
            complete: 'hijiffy_COMPLETE_posting_date',
            cancelled: 'hijiffy_CANCELLED_posting_date',
          },
        }
      ];

      await Promise.all(
        TRACKING_PROVIDERS.map((provider) =>
          this.processSingleTracking(provider, detailTransaction)
        )
      );
    },
    async processSingleTracking(provider, detailTransaction) {
      try {
        const merchant = this.getMerchant;
        const { status } = detailTransaction;
        const { type, configKeys, dateKeys } = provider
        const isRoutePaymentSuccess = this.$route.name === 'BePaymentResult';

        const isEnabled =
          merchant[configKeys.use] &&
          this.$isNotEmptyV2(merchant[configKeys.id]);

        if (!isEnabled) return;

        const pendingDate = detailTransaction[dateKeys.pending];
        const completeDate = detailTransaction[dateKeys.complete];
        const cancelledDate = detailTransaction[dateKeys.cancelled];

        const isPendingStatus = status === 'WAITING_PAYMENT_CONFIRMATION' || status === 'WAITING_CONFIRMATION'
        const isPendingPosted = isPendingStatus && this.$isNotEmptyV2(pendingDate);
        const isCompletePosted = status === 'COMPLETE' && this.$isNotEmptyV2(completeDate);
        const isCancelledPosted = status === 'CANCELLED' && this.$isNotEmptyV2(cancelledDate);
        const isHijiffyPending = type === 'hijiffy' && isPendingStatus

        if (isHijiffyPending || isPendingPosted || isCompletePosted || isCancelledPosted || !isRoutePaymentSuccess) return;

        const isCompleteReady = status === 'COMPLETE' && this.$isEmpty(completeDate);
        const isCancelledReady = status === 'CANCELLED' && this.$isEmpty(cancelledDate);

        let statusPosting = null;
        if (isCompleteReady) statusPosting = 'COMPLETE';
        else if (isCancelledReady) statusPosting = 'CANCELLED';

        const postedDate = await this.postingDateIntegration(type, statusPosting);

        if (postedDate) {
          if(type === 'hijiffy'){
            this.sendHijiffyTrack(detailTransaction);
            return;
          }
          if(type === 'google_tag_manager' && isCompleteReady) this.sendUserGuestData(detailTransaction);
          
          let eventName = 'payment_pending';
          if (isCompleteReady) eventName = 'purchase';
          if (isCancelledReady) eventName = 'transaction_cancelled';

          trackEvent(eventName, detailTransaction);
        }
      } catch (error) {
        console.error(`ERROR processSingleTracking(${provider.type}) :`, error.message);
      }
    },
    async postingDateIntegration(type, status = null){
      try {
        let params = {posting_date_types: type}
        if(status) params.status = status
        
        const apiResponse = await post("CORE_HOTEL", `tx-detail/${this.transactionId}/posting-date`, params);
        
        return apiResponse.data.success;
      } catch (error) {
        console.log("Error postingDateIntegration : ", error.message)
      }
    },
    sendUserGuestData(detailTransaction){
      const { transaction_details, payments, order_code } =  detailTransaction;

      const firstRoom = transaction_details[0];
      const { check_in_date: checkIn, check_out_date: checkOut, adult, child, infant } = firstRoom;
      
      const params = { checkIn, checkOut, adult, child, infant,
        totalPrice: Number(payments.total_payment),
        detailTransaction: transaction_details,
        invoiceNumber: order_code
      }
      this.$sendHotelUserGuestDataLayer("conversion", params);
    },
    sendHijiffyTrack(detailTransaction){
      const baseURL = "https://messenger-services-dhgsahdgashdga.com/pixel";
      const { payments: { total_payment }, merchant: { base_currency_code, name }, order_code, transaction_details } = detailTransaction;
      const { hijiffy_account_id } = this.getMerchant;
      
      const { check_in_date, check_out_date } = transaction_details[0];
      
      const formatDate = (date) => moment(date, 'YYYY-MM-DD').format('YYYYMMDD');
      
      const params = {
        accountID: hijiffy_account_id, 
        step: "Booking",    
        amount: Number(total_payment),
        currency: base_currency_code,
        property: name,
        bookingid: order_code,
        checkin: formatDate(check_in_date),
        checkout: formatDate(check_out_date)
      };

      const queryString = Object.keys(params)
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join('&');
      const finalURL = `${baseURL}?${queryString}`;

      const pixelImage = document.createElement('img');
      pixelImage.src = finalURL;
      pixelImage.width = 1;
      pixelImage.height = 1;

      if (document.body) {
        document.body.appendChild(pixelImage);
        console.log("Successfully added Hijiffy Booking Pixel tag to <body>.");
      } else {
        console.error("Failed to add Hijiffy Booking Pixel: <body> is not available");
      }
      
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/hotel/be/BePaymentResult.vue